import {
  Dialog,
  DialogContent,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import List from '@mui/material/List';
import { useCallback, useState } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components';
import { useCurrentUserProfile } from 'src/graphql';
import { useAiPrompts } from 'src/graphql/entities/ai-prompt';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useListKeyboardNavigation } from 'src/utils/hooks/use-list-keyboard-navigation';

export const AiPromptModal = () => {
  const { user } = useCurrentUserProfile();
  const [search, setSearch] = useState('');
  const { isOpened, modalProps } = useModalState(Modals.AiPrompts);
  const { closeModal } = useModalMethods(Modals.AiPrompts);
  const { aiPrompts, loading: isLoadingPrompts } = useAiPrompts();

  const filteredPrompts = aiPrompts.filter((prompt) =>
    prompt.friendlyName.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const showCustomOption =
    !isLoadingPrompts && search.trim() !== '' && filteredPrompts.length === 0;

  const items = showCustomOption
    ? [{ id: '', name: search, isCustom: true }]
    : filteredPrompts.map((p) => ({ id: p.id, name: p.friendlyName, isCustom: false }));

  const { selectedIndex, setSelectedIndex, registerItemRef, handleKeyDown } =
    useListKeyboardNavigation({
      itemCount: items.length,
      wrap: true,
      onEnter: (index) => {
        const selectedItem = items[index];
        if (!selectedItem) {
          return;
        }
        void handlePromptSelected(
          selectedItem.id,
          selectedItem.isCustom ? selectedItem.name : '',
        );
      },
    });

  const handleClose = useCallback(() => {
    closeModal();
    setSearch('');
    setSelectedIndex(0);
  }, [closeModal, setSelectedIndex]);

  async function handlePromptSelected(promptId: string, customPrompt = '') {
    if (!modalProps || isNullish(user?.id)) return;
    handleClose();

    const promptName =
      aiPrompts.find((prompt) => prompt.id === promptId)?.friendlyName || customPrompt;
    await modalProps?.onSubmit?.(promptId, promptName, customPrompt);
  }

  return (
    <Dialog
      open={isOpened}
      onClose={handleClose}
      PaperProps={{
        sx: {
          marginTop: '15%',
          alignSelf: 'flex-start',
        },
      }}
    >
      <DialogContent sx={{ padding: 0, width: '600px' }}>
        <TextField
          autoFocus
          variant="standard"
          size="small"
          fullWidth
          placeholder="Type a prompt or select an option..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelectedIndex(0);
          }}
          onKeyDown={(event) => {
            const handled = handleKeyDown(event);
            if (handled && event.key === 'Enter') {
              (event.currentTarget as HTMLInputElement)?.blur();
            }
          }}
          InputProps={{
            sx: {
              padding: '0.75rem',
            },
            startAdornment: (
              <InputAdornment position="start" sx={{ marginRight: '0.7rem' }}>
                <DynamicIcon
                  name="auto_awesome"
                  color={(theme) => theme.palette.custom.secondary.secondary60}
                />
              </InputAdornment>
            ),
          }}
        />

        <List
          sx={{
            padding: 0,
            maxHeight: '480px',
            overflowY: 'auto',
          }}
        >
          {isLoadingPrompts ? (
            <>
              <Skeleton variant="rectangular" sx={{ margin: 2 }} width="50%" />
              <Skeleton variant="rectangular" sx={{ margin: 2 }} width="80%" />
              <Skeleton variant="rectangular" sx={{ margin: 2 }} width="50%" />
              <Skeleton variant="rectangular" sx={{ margin: 2 }} width="60%" />
            </>
          ) : (
            <>
              {filteredPrompts.length > 0 && !showCustomOption && (
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => theme.palette.grey[700],
                    paddingLeft: 2,
                    textTransform: 'uppercase',
                  }}
                >
                  Ai Prompts
                </Typography>
              )}

              {items.map((item, index) => (
                <ListItemButton
                  key={item.id || 'custom'}
                  selected={index === selectedIndex}
                  onClick={() => handlePromptSelected(item.id, item.isCustom ? item.name : '')}
                  ref={registerItemRef(index)}
                >
                  <ListItemText
                    primary={
                      item.isCustom ? (
                        <span>
                          <b>Use custom prompt:</b> {item.name}
                        </span>
                      ) : (
                        item.name
                      )
                    }
                  />
                </ListItemButton>
              ))}
            </>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};
